import clsx from 'clsx';
import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import TimeZoneSelector from '@/core/TimeZoneSelector.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getFormFieldWrapperProps } from '@/formbuilder/formbuilder.utilities';
import type { Timezone } from '@/datetime/timezone.service';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { ImportDatafileTimezone } from '@/tools/importDatafile/importDatafile.store';
import type { BivariantCallback } from '@/utilities.types';
import type { TooltipPosition } from '@seeqdev/qomponents';

export interface TimeZoneSelectorIF extends ValidatingFormComponent<
  Timezone | ImportDatafileTimezone | { name: string } | undefined
> {
  component: 'TimeZoneSelectorFormComponent';
  onChange?: BivariantCallback<Timezone | ImportDatafileTimezone | { name: string } | undefined>;
  value?: Timezone | ImportDatafileTimezone | { name: string };
  optional?: boolean;
  disabled?: boolean;
  timezoneSet?: boolean;
  defaultTimeZone?: Timezone;
  direction?: 'top' | 'bottom' | 'auto';
  enableAutoDetect?: boolean;
  tooltipPlacement?: TooltipPosition;
}

export const TimeZoneSelectorFormComponent = (props: TimeZoneSelectorIF) => {
  const {
    name,
    validation,
    defaultTimeZone,
    skipToFormState,
    value,
    testId,
    enableAutoDetect = true,
    tooltipPlacement = 'left',
  } = props;

  const { t } = useTranslation();
  const defaultValidation = (timezoneObj: Timezone | ImportDatafileTimezone | { name: string } | undefined) =>
    _.isEmpty((timezoneObj as Timezone | { name: string } | undefined)?.name);
  const formState = useForm().getState();
  const timezoneSet = !_.isEmpty(formState.values[name]);

  return (
    <FormFieldWrapper {...getFormFieldWrapperProps(props, ['wrapperClassNames'])} wrapperClassNames="flexFillOverflow">
      <Field<Timezone | ImportDatafileTimezone | { name: string } | undefined>
        name={name}
        validate={validation || defaultValidation}>
        {({ input, meta }) => {
          const formFieldProps = getFormFieldProps(formState, input, meta, props);
          const { onChange } = formFieldProps;

          return (
            <>
              <TimeZoneSelector
                {...formFieldProps}
                onSelect={onChange}
                timezone={skipToFormState ? input.value : value}
                testId={testId ? `${testId}Field` : ''}
              />

              {enableAutoDetect && defaultTimeZone && (
                <div className="flexColumnContainer tw:mt-sq-5 tw:ml-sq-5">
                  <HoverTooltip placement={tooltipPlacement} text="USER.RESET_TOOLTIP">
                    <FakeLink
                      extraClassNames={clsx('cursorPointer', 'tw:italic', {
                        'font-size-smaller': timezoneSet,
                        'sq-text-danger': !timezoneSet,
                        'tw:underline!': !timezoneSet,
                        'force-link-look': timezoneSet,
                      })}
                      onClick={() => onChange(defaultTimeZone)}>
                      {t('USER.RESET')}
                    </FakeLink>
                  </HoverTooltip>
                </div>
              )}
            </>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
